import React from "react";
import { Box, Typography } from "@mui/material";
import PostCard from "../../components/common/RecentPostCard/PostCard";
import { useDispatch, useSelector } from "react-redux";
import { fetchBlogByIdAction, fetchBlogsAction } from "../../store/slices/blogSlice";
import BannerSection from "../../components/common/BannerSection";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import RecentBlogs from "./RecentBlogs";
import { DonateButtonWithArrowVariant } from "../../components/common/Button/Button";
import { useTheme, useMediaQuery } from "@mui/material";
import Breadcrumb from "../../components/common/Breadcrumb/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";

const BlogDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { byId, list } = useSelector((state) => state.blog);
  const blogFromStore = byId[id];
  const blogFromList = Array.isArray(list) ? list.find((b) => String(b.id) === String(id)) : undefined;
  const blog = blogFromStore || blogFromList || {};
  React.useEffect(() => {
    if (id) dispatch(fetchBlogByIdAction(id));
    if (!list || list.length === 0) dispatch(fetchBlogsAction());
  }, [dispatch, id]);
  const theme = useTheme();

  // check if screen is <600px
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // check if screen is >=1200px
  const isAbove1200 = useMediaQuery("(min-width:1200px)");
  const isBelow900 = useMediaQuery("(max-width: 899px)");

  // Reading time from API blog content (title + all section descriptions, strip HTML)
  const getReadingTime = () => {
    const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    const titleText = (blog?.title || "").trim();
    const descText = (blog?.BlogDescs || []).map((s) => stripHtml(s?.description || "")).join(" ");
    const text = `${titleText} ${descText}`.trim();
    const words = text ? text.split(/\s+/).length : 0;
    return words ? Math.max(1, Math.ceil(words / 200)) : 0;
  };
  const readingTime = getReadingTime();
  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Blog", path: "/blogs" },
          { label: "Blog Detail", path: null },
        ]}
      />

      <Box sx={{ mb: isMobile ? "10px" : "40px", mt: isMobile ? "0" : "30px" }}>
        <BannerSection
          title="Blogs Details"
          subtitle={isMobile ? "" : (blog?.title || "")}
        />
      </Box>
      <Box
        sx={{
          width: "94.44%",
          maxWidth: "1440px",
          margin: "0px auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: isBelow900 ? "0px":"26px",
          flexDirection: "row",
        }}
      >
        {/* Main Blog Content */}
        <Box
          sx={{
            mt: isMobile ? "10px" : 5,
            width: "100%",
            flex: 1,
          }}
        >
          {/* Blog Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: isMobile ? "18px" : isAbove1200 ? "36px" : "28px",
              fontWeight:isMobile ? "800" : 700,
              color: "#1D1D1B",
              mb: 3,
              lineHeight: isMobile ? "26px" :1.1,
              paddingBottom: isMobile ? "10px" : isAbove1200 ? "16px" : "22px",
              borderBottom: "0.5px solid #E8E8E8",
              letterSpacing:isMobile?" -0.18px": "-0.5px",
            }}
          >
            {blog?.title || ""}
          </Typography>
          {/* Duration and Posted On */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 4 }}>
            <Box sx={{ textAlign: "left" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : isAbove1200 ? "20px" : "16px",
                  fontWeight: 600,
                  lineHeight: 1,
                  color: "#141414",
                }}
              >
                Duration
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : isAbove1200 ? "20px" : "16px",
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: "#898989",
                  paddingTop: "12px",
                }}
              >
                {readingTime} min to read
              </Typography>
            </Box>
            <Box
              sx={{ borderLeft: "1.5px solid #B9B9B8", height: 49, mx: 2 }}
            />
            <Box sx={{ textAlign: "left" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : isAbove1200 ? "20px" : "16px",
                  fontWeight: 600,
                  lineHeight: 1,
                  color: "#141414",
                }}
              >
                Posted on
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : isAbove1200 ? "20px" : "16px",
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: "#898989",
                  paddingTop: "12px",
                }}
              >
                {new Date(blog?.createdAt || Date.now()).toLocaleDateString("en-US", { month: 'long', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>
          </Box>

          {/* Sections from BlogDescs */}
          {(blog?.BlogDescs || []).map((sec, idx) => (
            <Box key={sec.id || idx} sx={{ mb: 5 }}>
            {sec?.image && (
            <Box
              component="img"
              src={sec.image}
              alt={sec?.title || "Blog section"}
              sx={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                borderRadius: "16px",
                mb: 2,
                display: "block",
              }}
            />
            )}
            {sec?.title && (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? "14px" : isAbove1200 ? "22px" : "18px",
                color: "#1D1D1B",
                mb: 2,
              }}
            >
              {sec.title}
            </Typography>
            )}
            {sec?.description && (
           <Box
              component="div"
              sx={{
                fontSize: isMobile ? "12px" : isAbove1200 ? "16px" : "14px",
                color: "#686866",
                mb: 1,
                fontWeight: 400,
                lineHeight: 1.6,
                "& *": { color: "inherit" },
                "& p": { margin: "0 0 0.75em 0", "&:last-child": { marginBottom: 0 } },
                "& strong": { fontWeight: 700 },
                "& em": { fontStyle: "italic" },
                "& a": { color: "inherit", textDecoration: "underline" },
              }}
              dangerouslySetInnerHTML={{ __html: sec.description }}
            />
            )}
            {sec?.link && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 2,
                  mb: 2,
                }}
              >
                <DonateButtonWithArrowVariant
                  onClick={() => window.open(sec.link, '_blank', 'noopener')}
                  text="Donate Now"
                />
              </Box>
            )}
          </Box>
          ))}
        </Box>
        {/* Sidebar: Recent Posts */}
        <Box
          sx={{
            mt: 5,
            display: isBelow900 ? "none" : "flex",
            flexDirection: "column",
            position: "sticky",
            top: isMobile ? "80px" : "30px",
            alignSelf: "flex-start",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontSize: "18px",
              color: "#1D1D1B",
              fontWeight: 700,
              marginBottom: "20px",
            }}
          >
            Recent Posts
          </Typography>
          {(list || [])
            .filter((b) => String(b.id) !== String(id))
            .slice(0, 2)
            .map((b) => (
              <PostCard
                key={b.id}
                image={b.banner_image}
                title={b.title}
                date={b.createdAt}
                onClick={() => navigate(`/blog/${b.id}`)}
              />
            ))}
        </Box>
        <Box />
      </Box>
      <Box />
      <RecentBlogs />
      <Box />
      <Box>
        <NewsLogoSection />
      </Box>
    </>
  );
};

export default BlogDetails;
